import moment from 'moment'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { thirdPartyApi } from '../../../resources/hooks/api/thirdPartyApiHook'

// Standalone, read-only page that the backup alert opens in a new tab. It shows the
// per-entity breakdown of a single backup job (status, rows, retries, errors) without any
// internal artifact path. The job_id comes from the route (QBO_BACKUP_STATUS_PAGE) and is
// polled while the job is still running, then stops once it reaches a terminal state.
//
// gl-api wraps responses in ResponseModel, so the payload lives at resp.data.data.

const POLL_MS = 5000

const STATUS_BADGE: Record<string, string> = {
    completed: 'badge bg-success',
    failed: 'badge bg-danger',
    in_progress: 'badge bg-primary',
    pending: 'badge bg-secondary',
}

const badgeFor = (status: string) => STATUS_BADGE[status] || 'badge bg-secondary'

const QboBackupStatusPage: React.FC = () => {
    const { t, i18n } = useTranslation("scenes.thirdparty.quickbook.Dashboard")
    const { jobId } = useParams<{ jobId: string }>()
    const { qbFetchBackupStatus } = thirdPartyApi()
    const [job, setJob] = useState<any>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string>('')
    const interval = useRef<any>(null)

    useEffect(() => {
        if (!jobId) return

        const tick = () => {
            qbFetchBackupStatus({ job_id: jobId }, (_msg: string, resp: any) => {
                const data = resp?.data?.data
                setLoading(false)
                if (!data) return
                setJob(data)
                if (data.status === 'completed' || data.status === 'failed') {
                    clearInterval(interval.current)
                }
            }, (_msg: string, resp: any) => {
                setLoading(false)
                setError(resp?.data?.detail || resp?.data?.message || t('backup.statusLoadFailed', 'Could not load backup status'))
                clearInterval(interval.current)
            })
        }

        tick()
        interval.current = setInterval(tick, POLL_MS)
        return () => clearInterval(interval.current)
    }, [jobId])

    const entities = job?.entities || []
    const done = entities.filter((e: any) => e.status === 'completed').length
    const failed = entities.filter((e: any) => e.status === 'failed').length
    const rows = entities.reduce((sum: number, e: any) => sum + (e.rows_exported || 0), 0)

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3">
                    <div className="row align-items-center">
                        <div className="col-sm-12">
                            <h1 className="h5 font-weight-700 mb-0">
                                {t('backup.statusPageTitle', 'QuickBooks Backup Status')}
                            </h1>
                        </div>
                    </div>
                </div>

                {loading && !job && (
                    <div className="text-center p-4">
                        <i className="fa fa-spinner fa-spin fa-lg" aria-hidden="true"></i>
                    </div>
                )}

                {error && !job && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {job && (
                    <>
                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                            <span className={badgeFor(job.status)}>{t('backup.entityStatus.' + job.status, job.status) as string}</span>
                            <span>
                                {t('backup.summary', 'Entities')}: {done}/{entities.length}
                                &nbsp;&middot;&nbsp;{rows} {t('backup.rows', 'rows')}
                                &nbsp;&middot;&nbsp;{failed} {t('backup.failedCount', 'failed')}
                            </span>
                            {job.updated_at && (
                                <span className="text-muted ms-auto">
                                    {t('backup.updatedAt', 'Updated')}: {moment.utc(job.updated_at).local().locale(i18n.language).format('Do MMM YYYY, h:mm a')}
                                </span>
                            )}
                        </div>

                        <div className="table-responsive list-table">
                            <table className="table table-default">
                                <thead>
                                    <tr>
                                        <th>{t('backup.col.entity', 'Entity')}</th>
                                        <th>{t('backup.col.type', 'Type')}</th>
                                        <th>{t('backup.col.status', 'Status')}</th>
                                        <th className="text-end">{t('backup.col.rows', 'Rows')}</th>
                                        <th className="text-end">{t('backup.col.retries', 'Retries')}</th>
                                        <th>{t('backup.col.error', 'Error')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entities.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-center text-muted">
                                                {t('backup.noEntities', 'No entities yet')}
                                            </td>
                                        </tr>
                                    )}
                                    {entities.map((e: any, i: number) => (
                                        <tr key={i}>
                                            <td>{e.entity_name}</td>
                                            <td>{e.kind ? t('backup.kind.' + e.kind, e.kind) as string : '-'}</td>
                                            <td><span className={badgeFor(e.status)}>{t('backup.entityStatus.' + e.status, e.status) as string}</span></td>
                                            <td className="text-end">{e.rows_exported ?? 0}</td>
                                            <td className="text-end">{e.retry_count ?? 0}</td>
                                            <td className="text-danger">
                                                {e.error_message
                                                    ? `${e.error_category ? `[${t('backup.errorCategory.' + e.error_category, e.error_category)}] ` : ''}${e.error_message}`
                                                    : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default QboBackupStatusPage
